import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { apiConfig } from '@/config/api'
import { isChunkLoadFailure, reloadOnceForStaleChunk } from '@/utils/chunkLoadError'
import { rememberReturnPathBeforeError } from '@/utils/errorReturnPath'

const routes = [
  {
    path: '/callback',
    name: 'callback',
    component: () => import('@/views/CallbackView.vue'),
    meta: {
      title: 'Authentication Callback - CR Credit',
      requiresAuth: false,
    },
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/views/AccountView.vue'),
    meta: {
      title: 'Account - CR Credit',
    },
  },
  {
    path: '/',
    name: 'root',
    redirect: () => {
      return { name: 'home', params: { clientId: '0' } }
    },
  },
  {
    path: '/:clientId',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      title: 'Home - CR Credit',
    },
  },
  {
    path: '/:clientId/contracts',
    name: 'contracts',
    component: () => import('@/views/ContractsView.vue'),
    meta: {
      title: 'Smlouvy - CR Credit',
    },
  },
  {
    path: '/:clientId/cases',
    name: 'insurance-cases',
    component: () => import('@/views/InsuranceCasesView.vue'),
    meta: {
      title: 'Pojistné události - CR Credit',
    },
  },
  {
    path: '/:clientId/messages',
    name: 'messages',
    component: () => import('@/views/MessagesView.vue'),
    meta: {
      title: 'Zprávy - CR Credit',
    },
  },
  {
    path: '/:clientId/my-data',
    name: 'my-data',
    component: () => import('@/views/MyDataView.vue'),
    meta: {
      title: 'Moje údaje - CR Credit',
    },
  },
  {
    path: '/:clientId/change-password',
    name: 'change-password',
    component: () => import('@/views/ChangePasswordView.vue'),
    meta: {
      title: 'Změna hesla - CR Credit',
    },
  },
  {
    path: '/:clientId/contracts/:contractId',
    name: 'contract-detail',
    component: () => import('@/views/ContractDetailView.vue'),
    meta: {
      title: 'Detail smlouvy',
      resourceType: 'contract',
    },
  },
  {
    path: '/:clientId/subcontracts/:subcontractId',
    name: 'subcontract-detail',
    component: () => import('@/views/SubcontractDetailView.vue'),
    meta: {
      title: 'Detail podsmlouvy',
      resourceType: 'subcontract',
    },
  },
  {
    path: '/:clientId/cases/:caseId',
    name: 'insurance-case-detail',
    component: () => import('@/views/InsuranceCaseDetailView.vue'),
    meta: {
      title: 'Detail pojistné události',
      resourceType: 'insurance-case',
    },
  },
  {
    path: '/:clientId/contracts/:contractId/msg/:template?',
    name: 'new-message-contract',
    component: () => import('@/views/NewMessageView.vue'),
    meta: {
      title: 'Nová zpráva - CR Credit',
    },
  },
  {
    path: '/:clientId/subcontracts/:subcontractId/msg/:template?',
    name: 'new-message-subcontract',
    component: () => import('@/views/NewMessageView.vue'),
    meta: {
      title: 'Nová zpráva - CR Credit',
    },
  },
  {
    path: '/:clientId/cases/:caseId/msg/:template?',
    name: 'new-message-insurance-case',
    component: () => import('@/views/NewMessageView.vue'),
    meta: {
      title: 'Nová zpráva - CR Credit',
    },
  },
  {
    path: '/:clientId/msg/:template?',
    name: 'new-message',
    component: () => import('@/views/NewMessageView.vue'),
    meta: {
      title: 'Nová zpráva - CR Credit',
    },
  },
  {
    path: '/error',
    name: 'error',
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      title: 'Error',
      requiresAuth: false,
    },
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/LogoutView.vue'),
    meta: {
      title: 'Odhlášení - CR Credit',
      requiresAuth: false,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      title: 'Page Not Found - CR Credit',
      requiresAuth: false,
    },
  },
]

const router = createRouter({
  history: createWebHistory(apiConfig.baseURL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const { isAuthenticated, checkAuth, login, error } = useAuth()
  // console.log('Routing to:', to.name)

  // Fast check: read from localStorage first to avoid blocking navigation
  if (!isAuthenticated.value) {
    await checkAuth()
  }

  // Default behavior: require authentication unless explicitly marked as guest
  const requiresAuth = to.meta.requiresAuth !== false // Default to true

  // Handle routes that require authentication
  if (requiresAuth && !isAuthenticated.value) {
    // For unauthorized users, trigger OIDC login
    try {
      // Preserve intended destination to restore after authentication
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth.returnUrl', to.fullPath)
      }
    } catch {
      // ignore storage errors (e.g., disabled storage)
    }
    await login()
    if (error.value) {
      rememberReturnPathBeforeError(to.fullPath)
      next({ name: 'error' })
      return
    }
  }

  // Note: Route context syncing is handled in App.vue
  // where useCustomerStore() can be used safely (inside component setup)

  next()
})

router.onError((error) => {
  if (typeof window === 'undefined' || !isChunkLoadFailure(error)) {
    return
  }
  reloadOnceForStaleChunk()
})

export default router
